import {
  calculateEngagementFromCounts,
  EMPTY_ENGAGEMENT_RESULT,
  type EngagementResult,
} from '@/lib/engagement/calculate';

export type ManualEngagementInput = {
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  followers: number | null;
};

export type FollowerBand = {
  label: 'Below average' | 'Average' | 'Good' | 'Excellent';
  color: 'red' | 'amber' | 'green' | 'purple';
};

export type ManualEngagementFieldStrings = {
  views: string;
  likes: string;
  comments: string;
  shares: string;
  saves: string;
  followers: string;
};

const parseOptional = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
};

export function invalidManualEngagementFields(
  fields: ManualEngagementFieldStrings,
): Array<keyof ManualEngagementFieldStrings> {
  return (Object.keys(fields) as Array<keyof ManualEngagementFieldStrings>).filter((key) => {
    const trimmed = fields[key].trim();
    if (trimmed === '') return false;
    const value = Number(trimmed);
    return !Number.isFinite(value) || value < 0;
  });
}

export function parseManualEngagementFields(
  fields: ManualEngagementFieldStrings,
): ManualEngagementInput {
  return {
    views: parseOptional(fields.views),
    likes: parseOptional(fields.likes),
    comments: parseOptional(fields.comments),
    shares: parseOptional(fields.shares),
    saves: parseOptional(fields.saves),
    followers: parseOptional(fields.followers),
  };
}

export function calculateManualEngagement(input: ManualEngagementInput): EngagementResult {
  return calculateEngagementFromCounts({
    views: input.views,
    likes: input.likes,
    comments: input.comments,
    shares: input.shares,
    saves: input.saves,
    followers: input.followers,
  });
}

export function resolveManualEngagement(fields: ManualEngagementFieldStrings): {
  invalidFields: Array<keyof ManualEngagementFieldStrings>;
  parsed: ManualEngagementInput;
  result: EngagementResult;
  hasHeadline: boolean;
} {
  const invalidFields = invalidManualEngagementFields(fields);
  const parsed = parseManualEngagementFields(fields);
  const canComputeHeadline = invalidFields.every((key) => key === 'followers');
  const result = canComputeHeadline ? calculateManualEngagement(parsed) : EMPTY_ENGAGEMENT_RESULT;

  return {
    invalidFields,
    parsed,
    result,
    hasHeadline: canComputeHeadline && result.post !== null,
  };
}

export type CalculatorPlatform = 'instagram' | 'tiktok' | 'youtube';

const FOLLOWER_BANDS: Record<CalculatorPlatform, { below: number; average: number; good: number }> =
  {
    instagram: { below: 1, average: 3, good: 6 },
    tiktok: { below: 3, average: 8, good: 15 },
    youtube: { below: 0.5, average: 2, good: 5 },
  };

/** Directional bands for the optional follower-based comparison only.
 *  The public calculator no longer grades the reader with these labels. */
export function getFollowerBasedBand(
  ratePercent: number,
  platform: CalculatorPlatform = 'instagram',
): FollowerBand {
  const bands = FOLLOWER_BANDS[platform];
  if (ratePercent < bands.below) return { label: 'Below average', color: 'red' };
  if (ratePercent < bands.average) return { label: 'Average', color: 'amber' };
  if (ratePercent < bands.good) return { label: 'Good', color: 'green' };
  return { label: 'Excellent', color: 'purple' };
}
